// M9 audio facade: the integration API for the GameController / main.ts (see CLAUDE.md "M9 decisions").
//   const audio = createAudio({ data, settings });   // data: GameData (sfx, juice, sim) — arm BEFORE input
//   controller fan-out:  audio.onEvents(events, { silent });
//   Settings:            audio.setVolumes({ master, sfx }); audio.setMuted(b); audio.setSilentSwitchMode(m);
//   lifecycle (M11):     audio.hold(b) — hidden / ElsewhereOverlay / lock lost (independent of the Settings mute);
//   input (M6):          audio.bonk() — the router's refusal bonk, through the event mapper's shared bonk limiter.
// It arms the gesture unlock (audio/context.ts, the only AudioContext creator) unless an armed `handle` is
// passed, pre-renders the sample bank in idle time, builds every rendered AudioBuffer synchronously in the
// unlock gesture (rendering the `engine.unlockSounds` take 0s if the prewarm has not yet), maps SimEvents to
// sounds (eventMap.ts) and plays them through the SfxEngine.

import type { SimEvent } from '../sim/events.ts';
import { type AudioHandle, armAudio, type SessionType } from './context.ts';
import { type AudioData, createEventMapper, type EventMapper, mapperConfig } from './eventMap.ts';
import { createBankBuilder, type SoundId } from './recipes.ts';
import { hashString, mulberry32 } from './rng.ts';
import {
  createSfxEngine,
  type EngineStats,
  engineConfig,
  type PlayRequest,
  type SfxEngine,
  type VoiceInfo,
} from './SfxEngine.ts';

export type { SessionType } from './context.ts';
export type { AudioData } from './eventMap.ts';
export type { SoundId } from './recipes.ts';
export type { PlayRequest, VoiceInfo } from './SfxEngine.ts';

export interface AudioSettings {
  /** 0..1 */
  master: number;
  /** 0..1 */
  sfx: number;
  muted: boolean;
  /** 'ambient' respects the iPhone silent switch (default), 'playback' plays through it. */
  silentSwitch: SessionType;
}

export const DEFAULT_AUDIO_SETTINGS: Readonly<AudioSettings> = {
  master: 1,
  sfx: 1,
  muted: false,
  silentSwitch: 'ambient',
};

/**
 * The audio fields of a saved settings record (`SaveMeta.settings`, loosely typed): only well-formed values are
 * taken (volumes clamped to 0..1); everything else keeps its default. M7's Settings screen writes these names.
 */
export function audioSettingsFrom(x: unknown): Partial<AudioSettings> {
  if (typeof x !== 'object' || x === null) return {};
  const r = x as Record<string, unknown>;
  const out: Partial<AudioSettings> = {};
  const vol = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : null;
  const master = vol(r.master);
  if (master !== null) out.master = master;
  const sfx = vol(r.sfx);
  if (sfx !== null) out.sfx = sfx;
  if (typeof r.muted === 'boolean') out.muted = r.muted;
  if (r.silentSwitch === 'ambient' || r.silentSwitch === 'playback')
    out.silentSwitch = r.silentSwitch;
  return out;
}

export interface CreateAudioOptions {
  data: AudioData;
  settings?: Partial<AudioSettings>;
  /** Default: globalThis.window. */
  win?: Window;
  /** An unlock armed earlier (main.ts arms before the data load); default: createAudio arms its own. */
  handle?: AudioHandle;
  /** Wall clock in ms for the event rules (default: win.performance.now()). */
  now?: () => number;
  /** Seed of the take-pick / jitter RNG (default: a constant — audio is cosmetic, but reproducible). */
  seed?: number;
  /**
   * Sample pre-render: 'idle' (default: requestIdleCallback chunks, spaced setTimeout fallback), 'now' (all
   * takes synchronously; tests / tools) or 'none' (the unlock renders its sounds, the rest on first play).
   */
  prewarm?: 'idle' | 'now' | 'none';
}

export interface AudioDiag {
  /** Context state: 'none' before the first gesture, 'unsupported' without Web Audio. */
  state: string;
  session: string | null;
  armed: boolean;
  /** The lifecycle hold is on (hidden / another tab owns the game / lock lost). */
  held: boolean;
  /** AudioBuffers exist (the unlock ran). */
  ready: boolean;
  settings: AudioSettings;
  /** Sample takes still to render (0 = the bank is complete). */
  pendingTakes: number;
  /** Last requests that started a voice (newest last, ≤ 32). */
  recent: PlayRequest['sound'][];
  /** SimEvents received by onEvents, silent batches excluded (the controller's fan-out spy, §2.1). */
  received: number;
  voices: VoiceInfo[];
  stats: EngineStats;
}

export interface GameAudio {
  /** Fan-out of one drained event batch. `silent` (catch-up advance) skips everything. */
  onEvents(events: readonly SimEvent[], opts?: { silent?: boolean }): void;
  /** A player command without an event (pause, setTarget, paint…), for the "player-caused" stall bonk. */
  noteAction(bid?: number | null): void;
  /**
   * The input's refusal bonk (M6 router `sfx('bonk')`): ui.bonk through the event mapper's shared bonk limiter
   * — a dispatched drop whose `reject` event already bonked stays at one bonk. True when a voice started.
   */
  bonk(): boolean;
  /** One sound now, outside the sim (UI feedback: M7 buttons, the spike panel's test button). */
  play(sound: SoundId): boolean;
  /**
   * Page-lifecycle mute (M11 `AudioMute` hook: hidden, pagehide, ElsewhereOverlay, lock lost). Independent of
   * the Settings mute; while held nothing is scheduled and the context stays suspended — neither a gesture
   * nor `visible` resumes it (context.ts `hold`) — until released.
   */
  hold(on: boolean): void;
  setVolumes(v: { master?: number; sfx?: number }): void;
  setMuted(m: boolean): void;
  setSilentSwitchMode(m: SessionType): void;
  /** v0.2 music hook: duck the non-fanfare SFX and the music bus. */
  duck(db: number, ms: number): void;
  /** Forget combos / limiters / player actions (after a save load or reset). */
  reset(): void;
  diag(): AudioDiag;
  /** The unlock handle (for __gm.audio() and main.ts). */
  handle(): AudioHandle;
  dispose(): void;
}

const DEFAULT_SEED = 0x9e3779b9;

interface UserActivationLike {
  isActive: boolean;
}

export function createAudio(opts: CreateAudioOptions): GameAudio {
  const win = opts.win ?? window;
  const settings: AudioSettings = { ...DEFAULT_AUDIO_SETTINGS, ...opts.settings };
  const now = opts.now ?? (() => win.performance.now());
  const seed = opts.seed ?? DEFAULT_SEED;
  const { sfx } = opts.data;

  const bank = createBankBuilder(sfx);
  const engine: SfxEngine = createSfxEngine(engineConfig(sfx, opts.data.juice), bank, {
    rand: mulberry32(seed),
    clockMs: now,
  });
  const mapper: EventMapper = createEventMapper(
    mapperConfig(opts.data),
    mulberry32(seed ^ hashString('eventMap')),
  );
  engine.setVolumes({ master: settings.master, sfx: settings.sfx });

  const ownHandle = opts.handle === undefined;
  const handle = opts.handle ?? armAudio(win, { sessionType: settings.silentSwitch });
  if (!ownHandle) handle.setSessionType(settings.silentSwitch);
  const applyMute = (): void => engine.setMuted(settings.muted || handle.held());
  applyMute();
  /** Last gesture that created or resumed the context (its sounds may play while it is still resuming). */
  let gestureAt = Number.NEGATIVE_INFINITY;
  const offUnlock = handle.onUnlock((ctx) => {
    gestureAt = now();
    engine.attach(ctx); // synchronous: the sound of this same gesture can play
  });
  const offRegesture = handle.onRegesture(() => {
    gestureAt = now(); // back from the background / an iOS interruption: this tap's sound plays too
  });

  const recent: PlayRequest['sound'][] = [];
  let received = 0;
  let disposed = false;
  let idleId: number | null = null;
  let timeoutId: number | null = null;

  // ---- prewarm: render the sample takes while the player reads the screen
  type IdleWin = Window & {
    requestIdleCallback?: (
      cb: (d: { readonly didTimeout: boolean; timeRemaining(): number }) => void,
      o?: { timeout: number },
    ) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  const iw = win as IdleWin;
  const idleMin = sfx.engine.prewarmIdleMinMs;
  const schedule = (): void => {
    if (disposed || bank.done()) return;
    if (typeof iw.requestIdleCallback === 'function') {
      idleId = iw.requestIdleCallback(
        (d) => {
          idleId = null;
          // one take when the deadline allows it (or the 2 s timeout forced us), more while time is left
          if (d.didTimeout || d.timeRemaining() > idleMin) {
            do {
              if (!bank.step()) return;
            } while (d.timeRemaining() > idleMin);
          }
          schedule();
        },
        { timeout: 2000 },
      );
    } else if (typeof win.setTimeout === 'function') {
      // no requestIdleCallback (iOS Safari): one take per prewarmGapMs, never back-to-back long tasks
      timeoutId = win.setTimeout(() => {
        timeoutId = null;
        if (bank.step()) schedule();
      }, sfx.engine.prewarmGapMs);
    }
  };
  const prewarm = opts.prewarm ?? 'idle';
  if (prewarm === 'now') while (bank.step()) {}
  else if (prewarm === 'idle') schedule();

  /**
   * The input handler may run before the capture unlock (listener order): unlock — or re-unlock a context
   * that is not running — from inside the gesture. Skipped when a gesture already did it just now.
   */
  const unlockInGesture = (): void => {
    if (handle.state() === 'unsupported' || handle.held()) return;
    if (win.document.visibilityState === 'hidden') return;
    const ctx = handle.ctx();
    if (ctx !== null && (ctx.state === 'running' || ctx.state === 'closed')) return;
    if (ctx !== null && now() - gestureAt <= sfx.engine.unlockGraceMs) return;
    const ua = (win.navigator as Navigator & { userActivation?: UserActivationLike })
      .userActivation;
    if (ua?.isActive === true) handle.unlockNow();
  };

  const canPlay = (): boolean => {
    const ctx = handle.ctx();
    if (!ctx || !engine.attached() || settings.muted || handle.held()) return false;
    const state: string = ctx.state;
    if (state === 'running') return true;
    // a context created or resumed by this gesture is still resuming: its sounds start once it runs
    return (
      (state === 'suspended' || state === 'interrupted') &&
      now() - gestureAt <= sfx.engine.unlockGraceMs
    );
  };

  const playAll = (reqs: readonly PlayRequest[]): number => {
    if (!canPlay()) return 0;
    let n = 0;
    for (const r of reqs) {
      if (engine.play(r)) {
        n += 1;
        recent.push(r.sound);
        if (recent.length > 32) recent.shift();
      }
    }
    return n;
  };

  return {
    onEvents(events, o) {
      if (disposed || o?.silent === true || events.length === 0) return;
      received += events.length;
      unlockInGesture();
      if (!engine.attached()) return;
      playAll(mapper.map(events, now()));
    },
    bonk() {
      if (disposed) return false;
      unlockInGesture();
      const reqs = mapper.uiBonk(now()); // the limiter and the player action count even when nothing plays
      return engine.attached() && playAll(reqs) > 0;
    },
    play(sound) {
      if (disposed) return false;
      unlockInGesture();
      return engine.attached() && playAll([{ sound }]) > 0;
    },
    hold(on) {
      if (disposed) return;
      handle.hold(on);
      applyMute();
    },
    noteAction(bid = null) {
      mapper.noteAction(bid, now());
    },
    setVolumes(v) {
      if (v.master !== undefined) settings.master = v.master;
      if (v.sfx !== undefined) settings.sfx = v.sfx;
      engine.setVolumes(v);
    },
    setMuted(m) {
      settings.muted = m;
      applyMute();
    },
    setSilentSwitchMode(m) {
      settings.silentSwitch = m;
      handle.setSessionType(m);
    },
    duck(db, ms) {
      engine.duck(db, ms / 1000);
    },
    reset() {
      mapper.reset();
    },
    diag: () => ({
      state: handle.state(),
      session: handle.session(),
      armed: handle.armed(),
      held: handle.held(),
      ready: engine.attached(),
      settings: { ...settings },
      pendingTakes: bank.pending(),
      recent: [...recent],
      received,
      voices: engine.voices(),
      stats: engine.stats(),
    }),
    handle: () => handle,
    dispose() {
      if (disposed) return;
      disposed = true;
      if (idleId !== null) iw.cancelIdleCallback?.(idleId);
      if (timeoutId !== null) win.clearTimeout(timeoutId);
      offUnlock();
      offRegesture();
      engine.dispose();
      if (ownHandle) handle.destroy();
    },
  };
}
